import { useAppUpdateConfig, useMarketConfigs } from '../../features/settings';
import type { AppUpdatePlatformKey } from '../../features/settings/hooks/useAppUpdateConfig';
import type { PlatformAppUpdateConfigDTO } from '../../features/settings/types/settings.types';
import { formatDateTime } from '../../lib/format';

const inputCls =
  'h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';

const textareaCls =
  'min-h-[96px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';

function PlatformSection({
  label,
  platform,
  value,
  updateField,
}: {
  label: string;
  platform: AppUpdatePlatformKey;
  value: PlatformAppUpdateConfigDTO;
  updateField: <K extends keyof PlatformAppUpdateConfigDTO>(
    platform: AppUpdatePlatformKey,
    key: K,
    value: PlatformAppUpdateConfigDTO[K],
  ) => void;
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{label}</h2>
          <p className="mt-1 text-xs text-gray-500">独立控制最低版本、强更策略和下载地址。</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) => updateField(platform, 'enabled', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          启用更新检查
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-gray-700">最低版本号</label>
          <input
            type="text"
            value={value.min_version}
            onChange={(e) => updateField(platform, 'min_version', e.target.value)}
            className={inputCls}
            placeholder="例如 1.0.1"
            autoComplete="off"
          />
        </div>
        <div className="flex items-end">
          <label className="inline-flex h-10 items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={value.force_update}
              onChange={(e) => updateField(platform, 'force_update', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            低于最低版本时强制更新
          </label>
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm text-gray-700">{label} 下载地址</label>
          <input
            type="url"
            value={value.download_url}
            onChange={(e) => updateField(platform, 'download_url', e.target.value)}
            className={inputCls}
            placeholder="https://..."
            autoComplete="off"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-700">弹窗标题</label>
          <input
            type="text"
            value={value.title}
            onChange={(e) => updateField(platform, 'title', e.target.value)}
            className={inputCls}
            placeholder="留空使用默认标题"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-700">弹窗文案</label>
          <textarea
            value={value.message}
            onChange={(e) => updateField(platform, 'message', e.target.value)}
            className={textareaCls}
            placeholder="留空使用默认文案"
          />
        </div>
      </div>
    </section>
  );
}

export function AppUpdatePage() {
  const markets = useMarketConfigs();
  const { data, form, selectedMarketId, loading, saving, error, updatePlatformField, selectMarket, save, reload } =
    useAppUpdateConfig();

  if (loading || markets.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
      </div>
    );
  }

  if (error || markets.error) {
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error || markets.error}</p>
        <button onClick={() => { reload(); markets.reload(); }} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="space-y-2">
        <p className="text-sm text-gray-500">
          App 启动时会按当前设备平台读取对应配置；未启用的平台不会弹出更新提示。
        </p>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-5 text-amber-700">
          启用后必须填写对应平台的最低版本号和下载地址。强制更新时 App 只显示「去更新」按钮。
        </div>
      </div>

      <label className="block max-w-xs space-y-1">
        <span className="text-sm font-medium text-gray-700">配置地区</span>
        <select
          value={selectedMarketId}
          onChange={(e) => selectMarket(e.target.value)}
          className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {markets.items.map((market) => (
            <option key={market.market_id} value={market.market_id}>
              {market.market_id} · {market.name}
            </option>
          ))}
        </select>
      </label>

      <PlatformSection
        label="iOS"
        platform="ios"
        value={form.ios}
        updateField={updatePlatformField}
      />
      <PlatformSection
        label="Android"
        platform="android"
        value={form.android}
        updateField={updatePlatformField}
      />

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
        >
          {saving && (
            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          )}
          保存配置
        </button>
      </div>

      {data?.updated_at && (
        <div className="text-xs text-gray-400 space-y-1">
          <p>最后更新：{formatDateTime(data.updated_at)}</p>
          {data.updated_by != null && <p>操作人 ID：{data.updated_by}</p>}
        </div>
      )}
    </div>
  );
}
